import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { connect } from "react-redux";
import {
  Paper,
  Grid,
  TextField,
  Button,
  LinearProgress,
  Typography,
  Box,
  makeStyles,
  Backdrop,
  CircularProgress,
  Divider,
  Card,
  CardContent,
} from "@material-ui/core";
import { setBreadcrumps } from "../../actions";
import Header from "../../components/Header";
import axios from "../../api";
import Swal from "sweetalert2";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";

const useStyles = makeStyles((theme) => ({
  container: {
    padding: theme.spacing(3),
    margin: theme.spacing(2),
    backgroundColor: "transparent",
  },
  card: {
    borderRadius: 16,
    boxShadow: "0 8px 32px 0 rgba(31, 38, 135, 0.15)",
    backdropFilter: "blur(4px)",
    border: "1px solid rgba(255, 255, 255, 0.18)",
    marginTop: theme.spacing(3),
  },
  fileInput: {
    display: "none",
  },
  uploadButton: {
    margin: theme.spacing(1, 0),
    width: "100%",
    borderRadius: 12,
    textTransform: "none",
    padding: theme.spacing(1.5),
    border: `2px dashed ${theme.palette.divider}`,
    "&:hover": {
      border: `2px dashed ${theme.palette.primary.main}`,
      backgroundColor: theme.palette.action.hover,
    },
  },
  submitButton: {
    marginTop: theme.spacing(4),
    padding: theme.spacing(2),
    fontWeight: "bold",
    borderRadius: 12,
    fontSize: "1rem",
    textTransform: "none",
    boxShadow: "0 4px 14px 0 rgba(0, 118, 255, 0.39)",
  },
  progressContainer: {
    marginTop: theme.spacing(4),
    padding: theme.spacing(3),
    backgroundColor: theme.palette.action.selected,
    borderRadius: 12,
  },
  statusText: {
    marginTop: theme.spacing(2),
    fontWeight: "500",
    color: theme.palette.primary.main,
    textAlign: "center",
  },
  fileLabel: {
    display: "block",
    marginBottom: theme.spacing(1),
    fontWeight: "600",
    color: theme.palette.text.primary,
  },
  fileName: {
    fontSize: "0.85rem",
    color: theme.palette.success.main,
    wordBreak: "break-all",
    marginTop: theme.spacing(1),
    fontWeight: "500",
  },
  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
    color: "#fff",
    flexDirection: "column",
    gap: theme.spacing(2),
    backgroundColor: "rgba(0, 0, 0, 0.7)",
  },
  headerTitle: {
    fontWeight: 800,
    color: "#1a1a1a",
    fontWeight: 700,
    letterSpacing: "-2px",
  },
}));

function GraduatedImport(props) {
  const { setBreadcrumps, permission, token } = props;
  const classes = useStyles();
  const history = useHistory();

  const [baseMadreFile, setBaseMadreFile] = useState(null);
  const [leyendasFile, setLeyendasFile] = useState(null);
  const [graduationDate, setGraduationDate] = useState("");
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [statusMessage, setStatusMessage] = useState("");

  useEffect(() => {
    // Check permission (using 8 as base for Egresados)
    if (permission.includes(1) || permission.includes(8)) {
      setBreadcrumps([{ name: "Egresados" }, { name: "Importar Graduados" }]);
    } else {
      history.push("/");
      window.location.reload();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFileChange = (e, setter) => {
    const file = e.target.files[0];
    if (file) {
      setter(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!baseMadreFile || !leyendasFile || !graduationDate) {
      Swal.fire({
        icon: "warning",
        text: "Por favor complete todos los campos y seleccione los archivos requeridos.",
      });
      return;
    }

    setLoading(true);
    setProgress(10);
    setStatusMessage("Preparando archivos...");

    const formData = new FormData();
    formData.append("baseMadre", baseMadreFile);
    formData.append("leyendas", leyendasFile);
    formData.append("fechaGrado", graduationDate);

    // Sistema de Polling para dar feedback en vivo
    const checkStatus = async () => {
      try {
        const { data: status } = await axios.get("/graduated/import-status", {
          headers: { "access-token": token },
        });
        if (status && status.status !== "idle") {
          setProgress(status.percentage);
          setStatusMessage(status.message);
        }
      } catch (e) {
        console.error("Polling error:", e);
      }
    };

    const pollInterval = setInterval(checkStatus, 1500); // Polling cada 1.5s
    checkStatus(); // Llamada inmediata

    try {
      setStatusMessage(
        "Subiendo archivos y procesando... esto puede tardar unos minutos.",
      );

      const { data } = await axios.post("/graduated/import", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          "access-token": token,
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total,
          );
          // Si el servidor aún no reporta progreso propio, usamos el de subida (máx 50%)
          setProgress((prev) =>
            Math.max(prev, Math.min(percentCompleted / 2, 50)),
          );
        },
      });

      clearInterval(pollInterval);
      setProgress(100);
      setStatusMessage("Proceso completado exitosamente.");

      Swal.fire({
        icon: "success",
        title: "¡Importación Exitosa!",
        text: data.message || `Se han procesado los registros correctamente.`,
        confirmButtonColor: "#3085d6",
      }).then(() => {
        history.push("/graduated");
      });
    } catch (error) {
      console.error("Import error:", error);
      const errorMsg =
        error.response?.data?.message ||
        "Ocurrió un error inesperado durante la importación.";

      Swal.fire({
        icon: "error",
        title: "Error en la Importación",
        text: errorMsg,
      });

      clearInterval(pollInterval);
      setProgress(0);
      setStatusMessage("Error en el proceso.");
    } finally {
      setLoading(false);
    }
  };

  const handleRollback = async () => {
    const { value: selectedDate } = await Swal.fire({
      title: "Revertir Importación",
      input: "text",
      inputLabel: "Escriba la Fecha de Grado exacta a limpiar",
      inputValue: graduationDate,
      showCancelButton: true,
      inputPlaceholder: "Ej: 27 DE FEBRERO DE 2026",
      confirmButtonColor: "#d33",
      confirmButtonText: "Sí, Borrar Registros",
      footer: "Esto borrará solo los títulos de esta fecha y los egresados creados en esta sesión.",
    });

    if (selectedDate) {
      setLoading(true);
      setStatusMessage(`Limpiando registros del ${selectedDate}...`);
      try {
        const { data } = await axios.post(
          "/graduated/import-rollback",
          { fechaGrado: selectedDate },
          { headers: { "access-token": token } },
        );

        Swal.fire({
          icon: "success",
          title: "Limpieza Finalizada",
          html: `<b>Resultados:</b><br/>Títulos borrados: ${data.deletedTitles}<br/>Egresados nuevos purgados: ${data.deletedEgresados}`,
        });
      } catch (error) {
        Swal.fire({
          icon: "error",
          title: "Error al Revertir",
          text: error.response?.data?.message || "Algo salió mal al limpiar.",
        });
      } finally {
        setLoading(false);
      }
    }
  };

  const isFormValid = baseMadreFile && leyendasFile && graduationDate;

  return (
    <Paper elevation={0} className={classes.container}>
      <Header
        search={false}
        button={false}
        backButton={true}
        backRoute={"/graduated"}
        crump={[{ name: "Egresados" }, { name: "Importar" }]}
      />

      <Card className={classes.card}>
        <CardContent style={{ padding: 32 }}>
          <Box mb={4}>
            <Typography
              variant="h4"
              className={classes.headerTitle}
              gutterBottom
            >
              Importar Nuevos Graduados
            </Typography>
            <Typography variant="body1" color="textSecondary">
              Cargue los archivos maestros para enriquecer y registrar nuevos
              graduados en el sistema de forma masiva.
            </Typography>
          </Box>

          <Divider style={{ marginBottom: 32 }} />

          <form onSubmit={handleSubmit}>
            <Grid container spacing={4}>
              {/* Base Madre File */}
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" className={classes.fileLabel}>
                  1. Base Madre (Excel)
                </Typography>
                <input
                  accept=".xlsx, .xls"
                  className={classes.fileInput}
                  id="base-madre-file"
                  type="file"
                  onChange={(e) => handleFileChange(e, setBaseMadreFile)}
                />
                <label htmlFor="base-madre-file">
                  <Button
                    variant="outlined"
                    component="span"
                    className={classes.uploadButton}
                    startIcon={<CloudUploadIcon />}
                    disabled={loading}
                  >
                    {baseMadreFile
                      ? "Cambiar Archivo"
                      : "Seleccionar Base Madre"}
                  </Button>
                </label>
                {baseMadreFile && (
                  <Typography className={classes.fileName}>
                    ✓ {baseMadreFile.name}
                  </Typography>
                )}
              </Grid>

              {/* Leyendas File */}
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" className={classes.fileLabel}>
                  2. Leyendas (Excel)
                </Typography>
                <input
                  accept=".xlsx, .xls"
                  className={classes.fileInput}
                  id="leyendas-file"
                  type="file"
                  onChange={(e) => handleFileChange(e, setLeyendasFile)}
                />
                <label htmlFor="leyendas-file">
                  <Button
                    variant="outlined"
                    component="span"
                    className={classes.uploadButton}
                    startIcon={<CloudUploadIcon />}
                    disabled={loading}
                  >
                    {leyendasFile ? "Cambiar Archivo" : "Seleccionar Leyendas"}
                  </Button>
                </label>
                {leyendasFile && (
                  <Typography className={classes.fileName}>
                    ✓ {leyendasFile.name}
                  </Typography>
                )}
              </Grid>

              {/* Date Selection */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" className={classes.fileLabel}>
                  3. Fecha de Grado
                </Typography>
                <TextField
                  id="graduation-date"
                  type="text"
                  fullWidth
                  variant="outlined"
                  placeholder="Ej: 27 DE FEBRERO DE 2026"
                  value={graduationDate}
                  onChange={(e) => setGraduationDate(e.target.value)}
                  disabled={loading}
                  helperText="Copie y pegue el texto exacto de la columna fecha_grado del Excel"
                  InputProps={{
                    style: { borderRadius: 12 },
                  }}
                />
              </Grid>

              {/* Submit Button */}
              <Grid item xs={12}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  fullWidth
                  className={classes.submitButton}
                  disabled={loading || !isFormValid}
                >
                  {loading ? "Procesando..." : "Iniciar Proceso de Importación"}
                </Button>
              </Grid>

              {/* Rollback Button */}
              <Grid item xs={12}>
                <Button
                  variant="outlined"
                  color="secondary"
                  fullWidth
                  style={{ borderRadius: 12, textTransform: "none", marginTop: 8 }}
                  onClick={handleRollback}
                  disabled={loading}
                >
                  Limpiar Importación previa
                </Button>
              </Grid>
            </Grid>
          </form>

          {/* Progress Section */}
          {loading && (
            <Box className={classes.progressContainer}>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography
                  variant="body2"
                  color="primary"
                  style={{ fontWeight: 600 }}
                >
                  Progreso Actual
                </Typography>
                <Typography
                  variant="body2"
                  color="primary"
                  style={{ fontWeight: 600 }}
                >
                  {progress}%
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={progress}
                style={{ height: 10, borderRadius: 5 }}
              />
              <Typography variant="body2" className={classes.statusText}>
                {statusMessage}
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      <Backdrop className={classes.backdrop} open={loading}>
        <CircularProgress color="inherit" size={60} thickness={4} />
        <Typography variant="h6" style={{ marginTop: 20 }}>
          Procesando Importación...
        </Typography>
        <Typography variant="body2" style={{ opacity: 0.8 }}>
          {statusMessage}
        </Typography>
      </Backdrop>
    </Paper>
  );
}

const mapStateToProps = (state) => {
  return {
    token: state.token,
    permission: (state.permission || [])
      .filter((data) => data.modulosAcciones?.id_modulos === 8) // Module 8 is Egresados
      .map((item) => item.modulosAcciones?.id_acciones),
  };
};

const mapDispatchToProps = {
  setBreadcrumps,
};

export default connect(mapStateToProps, mapDispatchToProps)(GraduatedImport);
